import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { waybillsApi } from '../../api/waybill';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { formatDate } from '../../utils/formatDate';

export const WAYBILL_STATUS_LABELS: Record<string, string> = {
  preparing: 'Preparing', ready: 'Ready', dispatched: 'Dispatched', in_transit: 'In Transit', delivered: 'Delivered',
  partially_delivered: 'Partially Delivered', delivery_exception: 'Delivery Exception', returned: 'Returned', cancelled: 'Cancelled',
};

interface CreateWaybillForm {
  origin: string; destination: string; dispatchDate: string; recipientName: string; driverEmployeeId?: number; transportedBy?: string;
}

export function WaybillsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateWaybillForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['waybills'], queryFn: () => waybillsApi.list() });

  const createMutation = useMutation({
    mutationFn: (input: CreateWaybillForm) => waybillsApi.create({ ...input }),
    onSuccess: (result) => {
      showSuccess(`Waybill ${result.data.waybillReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['waybills'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/waybills/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const waybills = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Dispatch / Waybills" description="Track goods dispatch and delivery, with proof-of-delivery confirmation on arrival." />

      <SectionHeader title="Waybills" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Waybill'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Origin <input {...register('origin', { required: true })} /></label>
          <label>Destination <input {...register('destination', { required: true })} /></label>
          <label>Dispatch Date <input type="date" {...register('dispatchDate', { required: true })} /></label>
          <label>Recipient Name <input {...register('recipientName', { required: true })} /></label>
          <label>Driver <EmployeePicker {...register('driverEmployeeId', { valueAsNumber: true })} /></label>
          <label>Transported By (if not a VConnect vehicle) <input {...register('transportedBy')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load waybills. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={waybills}
          rowKey={(w) => w.id}
          columns={[
            { header: 'Reference', render: (w) => <a onClick={() => navigate(`/logistics/waybills/${w.id}`)} className="table-link">{w.waybill_reference}</a> },
            { header: 'Route', render: (w) => `${w.origin} → ${w.destination}` },
            { header: 'Recipient', render: (w) => w.recipient_name },
            { header: 'Dispatch Date', render: (w) => formatDate(w.dispatch_date) },
            { header: 'Status', render: (w) => <StatusBadge status={w.status} label={WAYBILL_STATUS_LABELS[w.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
